import React, { useEffect, useMemo, useState } from "react";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SimpleBar from "simplebar-react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "debounce";

import { selectCart, setCartState } from "../slices/cart/reducer";
import currencySymbol from "./currencySymbol";
import { postGetUserCart, postRemoveCartItem } from "../apiCalls/api_calls";
import { formatPriceValue } from "./ShopComponents";
import { getLanguageCode } from "../utils/shopHelpers";
import { useCustomerCart } from "../contexts/CustomerCartContext";

const MyCartDropdown = () => {
  const dispatch = useDispatch();
  const cartData = useSelector(selectCart);
  const user = useSelector((state) => state?.user?.currentUser);
  const { t, i18n } = useTranslation();
  const { customer: customerCartCtx } = useCustomerCart();
  const [isCartDropdown, setIsCartDropdown] = useState(false);
  // Z myszka: najechanie pokazuje podglad, klikniecie ikony przenosi do koszyka.
  // Na ekranie dotykowym telefon symuluje najechanie przy dotknieciu - podglad mignal i od razu bylo przejscie
  // do koszyka. Tam dotkniecie tylko otwiera/zamyka podglad, a do koszyka prowadza przyciski w podgladzie.
  const canHover = useMemo(() => window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? true, []);

  // Stable across renders - a debounce instance recreated every render can't track its own
  // pending timer, so a hover-triggered open could still fire after the dropdown was closed.
  const onMouseEnter = useMemo(() => debounce(() => setIsCartDropdown(true), 50), []);
  const onMouseLeave = () => {
    setIsCartDropdown(false);
    onMouseEnter.clear();
  };

  const toggleCartDropdown = () => {
    setIsCartDropdown((prev) => !prev);
  };

  // Used by the "cart"/"checkout" buttons: always close (never reopen) when navigating away,
  // and cancel any pending hover-open so it can't reopen the dropdown right after navigation.
  const closeCartDropdown = () => {
    onMouseEnter.clear();
    setIsCartDropdown(false);
  };

  useEffect(() => {
    if (!user?.token) {
      return;
    }

    const loadCart = async () => {
      try {
        const response = await postGetUserCart({ validate: false, lang: getLanguageCode() });
        dispatch(setCartState(response.data?.cart || {}));
      } catch (error) {
        console.log(error);
      }
    };

    loadCart();
  }, [dispatch, user?.token, i18n.language]);

  const removeCartItem = async (cartItemId) => {
    try {
      const response = await postRemoveCartItem({ cart_item_id: cartItemId, lang: getLanguageCode() });
      dispatch(setCartState(response.data?.cart || {}));
    } catch (error) {
      console.log(error);
    }
  };

  const cartBadge = (
    <span className="position-absolute cartitem-badge topbar-badge fs-10 translate-middle badge rounded-pill bg-info">
      {cartData.quantity || 0}
    </span>
  );

  return (
    <Dropdown
      isOpen={isCartDropdown}
      toggle={toggleCartDropdown}
      className="topbar-head-dropdown ms-1 header-item"
      onMouseOver={canHover ? onMouseEnter : undefined}
      onMouseLeave={canHover ? onMouseLeave : undefined}
    >
      {canHover ? (
        <Link to="/cart">
          <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle shadow-none">
            <i className="bx bx-cart fs-22"></i>
            {cartBadge}
          </DropdownToggle>
        </Link>
      ) : (
        <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle shadow-none" aria-label={t('cartDropdown.title')}>
          <i className="bx bx-cart fs-22"></i>
          {cartBadge}
        </DropdownToggle>
      )}
      <DropdownMenu className="dropdown-menu-xl dropdown-menu-end p-0 dropdown-menu-cart" aria-labelledby="page-header-cart-dropdown">
        <div className="p-3 border-top-0 border-start-0 border-end-0 border-dashed border">
          <Row className="align-items-center">
            <Col>
              <h6 className="m-0 fs-16 fw-semibold">{t('cartDropdown.title')}</h6>
            </Col>
            <div className="col-auto">
              <span className="badge bg-warning-subtle text-warning fs-13">
                {t('cartDropdown.items', { count: cartData.quantity || 0 })}
              </span>
            </div>
          </Row>
        </div>
        <SimpleBar style={{ maxHeight: "300px" }}>
          <div className="p-2">
            {!cartData?.products?.length ? (
              <div className="text-center empty-cart">
                <div className="avatar-md mx-auto my-3">
                  <div className="avatar-title bg-info-subtle text-info fs-36 rounded-circle">
                    <i className="bx bx-cart"></i>
                  </div>
                </div>
                <h5 className="mb-3">{t('cartDropdown.empty')}</h5>
              </div>
            ) : (
              <>
                {cartData.products.map((item, index) => (
                  <div className="d-block dropdown-item text-wrap dropdown-item-cart px-3 py-2" key={item.cart_item_id || index}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1 fs-14">
                          <Link to="/cart" className="text-reset" onClick={closeCartDropdown}>{item.name}</Link>
                        </h6>
                        <p className="mb-0 fs-12 text-muted">
                          {t('cartDropdown.quantity')} <span>{item.quantity} x {formatPriceValue(item.user_price)} {currencySymbol(item.currency)}</span>
                        </p>
                      </div>
                      <div className="px-2">
                        <h5 className="m-0 fw-normal">
                          <span className="cart-item-price">{formatPriceValue(Number(item.quantity || 0) * Number(item.user_price || 0))}</span> {currencySymbol(item.currency)}
                        </h5>
                      </div>
                      {/* <div className="ps-2">
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-ghost-secondary remove-item-btn"
                          onClick={() => removeCartItem(item.cart_item_id)}
                        >
                          <i className="ri-close-fill fs-16"></i>
                        </button>
                      </div> */}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </SimpleBar>
        <div className="p-3 border-bottom-0 border-start-0 border-end-0 border-dashed border" id="checkout-elem">
          <div className="d-flex justify-content-between align-items-center pb-3">
            <h5 className="m-0 text-muted">{t('cartDropdown.sum')}</h5>
            <div className="px-2">
              <h5 className="m-0">
                <span id="cart-item-total">{formatPriceValue(cartData.total)} {currencySymbol(cartData.products?.[0]?.currency)}</span>
              </h5>
            </div>
          </div>

          <Link to="/cart" className="btn btn-primary text-center w-100 mb-2" onClick={closeCartDropdown}>
            <i className="bx bx-cart fs-14"></i> {t('cartDropdown.cartButton')}
          </Link>
          {!customerCartCtx && (
          <Link
            to="/checkout"
            className={"btn btn-primary text-center w-100" + (cartData?.products?.length === 0 || cartData?.hasIssues ? " disabled" : "")}
            onClick={closeCartDropdown}
          >
            <i className="bx bx-list-check fs-14"></i> {t('cartDropdown.orderButton')}
          </Link>
          )}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default MyCartDropdown;
